import * as React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Plus, RefreshCcw, Save, Loader, XCircle } from '@lifeomic/chromicons';
import {
  useActiveAccount,
  useActiveProjectId,
  useNotify,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { VariantSelectionActions } from './VariantSelectionActions';
import SelectedCountPillOE from './SelectedCountPillOE';
import { useGenomicsReports } from '../useGenomicsReports';
import { useParams, Prompt } from 'react-router-dom';
import DiagnosticReport = fhir.DiagnosticReport;
import { SelectedShortVariant } from '../types';
import { VariantSelection } from '../useSelectedVariants';
import { DateTime } from 'luxon';
import { directReporting } from '../../../../../src/util/errorReporting';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { SelectedFilterOption } from '../../../../../src/components/common/Genomics/SelectedFilterOptionsContext';
import { apiHooks } from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';
import { SequenceType, Test } from '../../types';

export const useStyles = makeStyles((theme: Theme) => ({
  // copied from SubjectViewer ActionsContainer -> abstract to a shared place?
  actionsRoot: {
    display: 'flex',
    flexWrap: 'wrap',
    flexGrow: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.5, 2, 0.5, 1),
    background: theme.palette.common.white,
    minHeight: theme.pxToRem(51),
  },
  addReport: {
    marginRight: '0.5rem',
  },
  buttonContainer: {
    flex: 1,
  },
  menusContainer: {
    padding: theme.spacing(0.125, 0),
  },
  pillContainer: {
    display: 'flex',
    margin: theme.spacing(0.125, -0.375, 0),
    '& span': {
      margin: theme.spacing(0.375),
      whiteSpace: 'nowrap',
    },
    minHeight: '35px',
    marginLeft: 'auto',
  },
  show: {
    display: 'block',
  },
  hide: {
    display: 'none',
  },
}));

const messages = defineMessages({
  addVariants: {
    id: 'tableColumns.addVariants',
    defaultMessage: 'Save {count, plural, one {Variant} other {Variants}}',
  },
  addReport: {
    id: 'tableColumns.addReport',
    defaultMessage: 'Create Note',
  },
  alertAddSuccess: {
    id: 'useVariantAlert.alert.add.success',
    defaultMessage:
      'Added {count} {count, plural, one {Variant} other {Variants}} to {report}',
  },
  alertBothSuccess: {
    id: 'useVariantAlert.alert.both.success',
    defaultMessage:
      'Added {addCount} {addCount, plural, one {Variant} other {Variants}}, removed {removeCount} {removeCount, plural, one {Variant} other {Variants}} from {report} ',
  },
  alertRemoveSuccess: {
    id: 'useVariantAlert.alert.remove.success',
    defaultMessage:
      'Removed {count} {count, plural, one {Variant} other {Variants}} from {report}',
  },
  alertVariantsError: {
    id: 'useVariantAlert.alert.error.variants',
    defaultMessage: 'Something went wrong when adding or removing variants',
  },
  alertCreateDraftSuccess: {
    id: 'useVariantAlert.alert.createDraft.success',
    defaultMessage: "Draft created. If it doesn't appear, please refresh.",
  },
  alertCreateDraftError: {
    id: 'useVariantAlert.alert.error.createDraft',
    defaultMessage:
      'Something went wrong when trying to create the draft. Please refresh and try again.',
  },
  alertGenerateHereditaryReportSuccess: {
    id: 'useVariantAlert.alert.generateHereditaryReport.success',
    defaultMessage:
      'Hereditary disease risk screen report autopopulated successfully.',
  },
  alertGenerateHereditaryReportError: {
    id: 'useVariantAlert.alert.error.generateHereditaryReport',
    defaultMessage:
      'Something went wrong when trying to autopopulate the hereditary disease risk screen report.',
  },
  promptUnsavedVariants: {
    id: 'useVariantAlert.prompt.unsavedVariants',
    defaultMessage:
      'You have unsaved variants, are you sure you want to leave this page and discard your changes?',
  },
  referencedResourceNotFound: {
    id: 'useVariantAlert.prompt.referencedResourceNotFound',
    defaultMessage:
      'Unable to retrieve targeted variants referenced in genomics report.',
  },
  populateHdrReport: {
    id: 'actionsContainer.populateHdrReport',
    defaultMessage: 'Populate HDR Report',
  },
  populateHdrReportLoading: {
    id: 'actionsContainer.populateHdrReportLoading',
    defaultMessage: 'Populating HDR Report',
  },
  populateHdrReportTooltip: {
    id: 'actionsContainer.populateHdrReportTooltip',
    defaultMessage:
      'Auto-populate the Hereditary Disease Risk Screen Report for this subject.',
  },
  clearHdrReport: {
    id: 'actionsContainer.clearHdrReport',
    defaultMessage: 'Clear Previous Auto-Population',
  },
  clearHdrReportLoading: {
    id: 'actionsContainer.clearHdrReportLoading',
    defaultMessage: 'Clearing Previous Auto-Population',
  },
  clearPopulateHdrReportTooltip: {
    id: 'actionsContainer.clearPopulateHdrReportTooltip',
    defaultMessage:
      'Clear the Hereditary Disease Risk Screen Report auto population resources for this subject.',
  },
});

const DRAFT_DIAGNOSTIC_REPORT_STATUS = ['registered', 'preliminary'];

interface ActionsContainerOEProps {
  isVariantSelectionEnabled: boolean;
  isMTBNotesEnabled: boolean;
  selectDiagnosticReport: (report: DiagnosticReport) => Promise<string[]>;
  variantSelection: VariantSelection;
  createTargetedVariant: (variant: SelectedShortVariant) => Promise<string>;
  selectedVariantError: boolean;
  selectedFilterOptions: SelectedFilterOption[];
  activeTest: Test;
  selectedSequenceType: SequenceType;
}

const ActionsContainerOE: React.FC<ActionsContainerOEProps> = ({
  isVariantSelectionEnabled,
  selectDiagnosticReport,
  variantSelection,
  createTargetedVariant,
  selectedVariantError,
  isMTBNotesEnabled,
  selectedFilterOptions,
  activeTest,
  selectedSequenceType,
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();
  const projectId = useActiveProjectId();
  const activeAccount = useActiveAccount();
  const isFountainLifeAccount = !!activeAccount?.id?.startsWith('fountainlife');
  const { subjectId } = useParams<{ subjectId: string }>();
  const [creatingDraft, setCreatingDraft] = React.useState<boolean>(false);
  const [newDraft, setNewDraft] = React.useState<boolean>(false);
  const [saveDisabled, setSaveDisabled] = React.useState<boolean>(false);
  const [refresh, setRefresh] = React.useState<boolean>(false);
  const [notify] = useNotify();
  const [currentDiagnosticReport, setCurrentDiagnosticReport] =
    React.useState<DiagnosticReport>();
  const [newSelection, setNewSelection] = React.useState<string[]>();
  const [overallInterpretations, setOverallInterpretations] = React.useState<
    string[]
  >([]);
  const {
    genomicsReports,
    refreshGenomicsReports,
    newGenomicReport,
    updateDiagnosticReport,
  } = useGenomicsReports({
    isVariantSelectionEnabled,
    datasetId: projectId,
    subjectId,
  });
  const {
    mutate: generateHereditaryReport,
    isLoading: isGeneratingHereditaryReport,
  } = apiHooks.useAPIMutation('POST /v1/genomics-report/generate/hereditary', {
    onError: () => {
      notify(
        formatMessage(messages.alertGenerateHereditaryReportError),
        'error',
      );
    },
    onSuccess: () => {
      notify(
        formatMessage(messages.alertGenerateHereditaryReportSuccess),
        'success',
      );
    },
  });

  const hasDraft = React.useMemo(() => {
    return !!genomicsReports.find(diagnosticReport =>
      DRAFT_DIAGNOSTIC_REPORT_STATUS.includes(diagnosticReport.status),
    );
  }, [genomicsReports]);

  React.useEffect(() => {
    if (hasDraft) {
      setCreatingDraft(false);
    }
  }, [hasDraft]);

  const createDraft = async () => {
    try {
      setCreatingDraft(true);
      await newGenomicReport();
      const newDateQuery = `le${DateTime.now().toISO()}`;
      setTimeout(() => {
        refreshGenomicsReports({
          dateQuery: newDateQuery,
          subjectId,
        });
        notify(formatMessage(messages.alertCreateDraftSuccess), 'success');
        setNewDraft(true);
      }, 2000); // This 2 second timeout greatly increases the chance that the draft list will reload correctly
    } catch (e) {
      notify(formatMessage(messages.alertCreateDraftError), 'error');
    }
  };

  const prevSelection = React.useMemo(() => {
    return currentDiagnosticReport?.result
      ? currentDiagnosticReport.result
          .map(variant => {
            return variant.reference;
          })
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          .filter(reference => !overallInterpretations.includes(reference))
      : [];
  }, [currentDiagnosticReport?.result, overallInterpretations]);

  const initSelection = React.useCallback(() => {
    setNewSelection(Object.keys(variantSelection.variants));
  }, [variantSelection]);

  React.useEffect(() => {
    initSelection();
  }, [initSelection]);

  const readyForSave = React.useMemo(() => {
    if (selectedVariantError) {
      return false;
    }
    if (
      !currentDiagnosticReport ||
      !DRAFT_DIAGNOSTIC_REPORT_STATUS.includes(currentDiagnosticReport.status)
    ) {
      return false;
    }
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (prevSelection.length !== newSelection.length) {
      return true;
    } else {
      for (let i = 0; i < prevSelection.length; i++) {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        if (prevSelection[i] !== newSelection[i]) {
          return true;
        }
      }
      return false;
    }
  }, [
    prevSelection,
    newSelection,
    currentDiagnosticReport,
    selectedVariantError,
  ]);

  const selectGenomicsReport = React.useCallback(
    async (diagnosticReport: DiagnosticReport) => {
      try {
        const references = await selectDiagnosticReport(diagnosticReport);

        setOverallInterpretations(references || []);
      } catch (error) {
        console.error(
          `Unable to retrieve targeted variants referenced in genomics report ${
            diagnosticReport?.id
          }; ${error.toString()}`,
        );
      }
      setCurrentDiagnosticReport(diagnosticReport);
    },
    [selectDiagnosticReport],
  );

  React.useEffect(() => {
    if (selectedVariantError) {
      notify(formatMessage(messages.referencedResourceNotFound), 'error');
      directReporting.reportInfoToBugsnag(
        `Unable to retrieve targeted variants referenced in genomics report.`,
      );
    }
  }, [selectedVariantError, notify, formatMessage]);

  const findVariantDelta = (list1: string[], list2: string[]) => {
    return list1.filter(variant => {
      if (list2.indexOf(variant) === -1) return variant;
    });
  };

  // if unsaved changes, prevent navigation external to the app (reload or change to the url)
  // currently no way to customize the message shown - it's browser-defined
  React.useEffect(() => {
    const unloadCallback = (event: any) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    if (readyForSave) {
      window.addEventListener('beforeunload', unloadCallback);
      return () => window.removeEventListener('beforeunload', unloadCallback);
    } else {
      window.removeEventListener('beforeunload', unloadCallback);
    }
  }, [readyForSave, formatMessage]);

  const addVariantsToGenomicsReport = async () => {
    setSaveDisabled(true);
    let reportName = '';
    const prevVariants = currentDiagnosticReport?.result
      ? currentDiagnosticReport.result
          .map(variant => {
            return variant.reference;
          })
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          .filter(ref => !overallInterpretations.includes(ref))
      : [];
    try {
      await variantSelection.persist(createTargetedVariant);
      let message = undefined;
      let args = undefined;
      const newVariants = Object.keys(variantSelection.variants);
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const addedVariants = findVariantDelta(newVariants, prevVariants);
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const removedVariants = findVariantDelta(prevVariants, newVariants);
      setNewSelection(newVariants);

      setCurrentDiagnosticReport(report => {
        // combine variant selections and overall interpretations
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        report.result = [
          ...variantSelection.references,
          ...overallInterpretations,
        ].map(reference => {
          return { reference };
        });

        reportName =
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          new Date(report.effectiveDateTime).toDateString() + ' MTB Notes';
        return report;
      });
      if (addedVariants.length && removedVariants.length) {
        message = messages.alertBothSuccess;
        args = {
          addCount: addedVariants.length,
          removeCount: removedVariants.length,
        };
      } else if (addedVariants.length) {
        message = messages.alertAddSuccess;
        args = { count: addedVariants.length };
      } else if (removedVariants) {
        message = messages.alertRemoveSuccess;
        args = { count: removedVariants.length };
      }
      notify(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        formatMessage(message, {
          ...args,
          report: reportName,
        }),
        'success',
      );
      setSaveDisabled(false);
    } catch (e) {
      console.error(e);
      notify(formatMessage(messages.alertVariantsError), 'error');
    }

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return await updateDiagnosticReport(currentDiagnosticReport);
  };

  const dateQuery = `le${DateTime.now().toISO()}`;

  return (
    <div className={classes.actionsRoot}>
      {/* if unsaved changes, prevent navigation internal to the app, i.e.
      clicking another page link */}
      <Prompt
        when={readyForSave}
        message={formatMessage(messages.promptUnsavedVariants)}
      />

      {isMTBNotesEnabled && (
        <Box className={classes.buttonContainer} justify="space-between">
          <Box className={classes.menusContainer} align={'center'}>
            {!creatingDraft && !hasDraft && (
              <Button
                icon={Plus}
                onClick={createDraft}
                className={classes.addReport}
              >
                {formatMessage(messages.addReport)}
              </Button>
            )}

            {creatingDraft && !hasDraft && (
              <Box padding={1}>
                <DotLoader size={0} />
              </Box>
            )}

            {refresh && (
              <IconButton
                icon={RefreshCcw}
                aria-label={'Refresh'}
                onClick={() => {
                  refreshGenomicsReports({ dateQuery, subjectId });
                  setRefresh(false);
                }}
              />
            )}

            <VariantSelectionActions
              genomicsReports={genomicsReports}
              selectGenomicsReport={selectGenomicsReport}
              newDraft={newDraft}
              setNewDraft={setNewDraft}
            />
          </Box>
        </Box>
      )}

      <div className={classes.pillContainer}>
        {readyForSave && isMTBNotesEnabled && (
          <Button
            disabled={saveDisabled}
            icon={Save}
            variant="text"
            onClick={addVariantsToGenomicsReport}
          >
            {formatMessage(messages.addVariants, {
              count: variantSelection.count,
            })}
          </Button>
        )}

        {isGeneratingHereditaryReport && <DotLoader size={0} />}

        {isFountainLifeAccount &&
          variantSelection.count === 0 &&
          !!selectedFilterOptions.length && (
            <Tooltip
              title={formatMessage(messages.clearPopulateHdrReportTooltip)}
            >
              <Button
                disabled={isGeneratingHereditaryReport}
                icon={isGeneratingHereditaryReport ? Loader : XCircle}
                variant="text"
                onClick={() => {
                  const shortVariantSet = activeTest?.sets?.find(
                    set =>
                      set.sequenceType === selectedSequenceType &&
                      set.setType === 'shortVariant',
                  );

                  generateHereditaryReport({
                    patientId: subjectId,
                    variantSetId: shortVariantSet?.id || '',
                    // Providing an empty array will clear the auto population fhir resource
                    // previously created, if any, except for the sample id.
                    // TODO: update react-client client service version
                    variantIds: [] as unknown as [string, ...string[]],
                  });
                }}
              >
                {isGeneratingHereditaryReport
                  ? formatMessage(messages.clearHdrReportLoading)
                  : formatMessage(messages.clearHdrReport)}
              </Button>
            </Tooltip>
          )}

        {isFountainLifeAccount &&
          !!variantSelection.count &&
          !!selectedFilterOptions.length && (
            <Tooltip title={formatMessage(messages.populateHdrReportTooltip)}>
              <Button
                disabled={isGeneratingHereditaryReport}
                icon={isGeneratingHereditaryReport ? Loader : Plus}
                variant="text"
                onClick={() => {
                  const shortVariantSet = activeTest?.sets?.find(
                    set =>
                      set.sequenceType === selectedSequenceType &&
                      set.setType === 'shortVariant',
                  );

                  generateHereditaryReport({
                    patientId: subjectId,
                    variantSetId: shortVariantSet?.id || '',
                    variantIds: Object.keys(variantSelection.variants) as [
                      string,
                      ...string[],
                    ],
                  });
                }}
              >
                {isGeneratingHereditaryReport
                  ? formatMessage(messages.populateHdrReportLoading)
                  : formatMessage(messages.populateHdrReport)}
              </Button>
            </Tooltip>
          )}

        <SelectedCountPillOE
          selectedCount={variantSelection.count + overallInterpretations.length}
        />
      </div>
    </div>
  );
};

export default React.memo(ActionsContainerOE);
